/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import invariant from 'react-utils/invariant';
import { fetchUsersAndTeams } from 'sales-content-partitioning/api/UsersAndTeamsApi';
import { fetchPermissionsForContent, saveContentPermissions } from 'sales-content-partitioning/api/ContentPermissionsApi';
import { canAssignContent } from 'sales-content-partitioning/lib/Permissions';
import { toggleSelectedTeam } from 'sales-content-partitioning/lib/TeamHelpers';
import { PRIVATE, EVERYONE, SPECIFIC } from 'sales-content-partitioning/constants/SharingOptionTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { rethrowError } from 'UIComponents/core/PromiseHandlers';
import { H2 } from 'foundations-components/transitional/components/H2';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Section } from 'foundations-components/transitional/components/Section';
import AssignmentPanelOptions from './assignmentPanel/AssignmentPanelOptions';
import AssignmentPanelTabs from './assignmentPanel/AssignmentPanelTabs';
import AssignmentPanelSaveButton from './assignmentPanel/AssignmentPanelSaveButton';
import AssignmentPanelErrorState from './assignmentPanel/AssignmentPanelErrorState';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function toggleSelectUser(selectedUsers, userId) {
  if (selectedUsers.includes(userId)) {
    return selectedUsers.filter(id => id !== userId);
  }
  return selectedUsers.concat([userId]);
}
export const AssignmentPanelInstances = {};
export const DEFAULT_PANEL_ID = 'default';
const defaultState = {
  isOpen: false,
  isContentPermissionLoading: true,
  isContentPermissionError: false,
  ownerId: null,
  // Cast for easier typing
  objectId: null,
  // Cast for easier typing
  objectType: null,
  // Cast for easier typing
  userSearch: '',
  initialSharingOption: EVERYONE,
  initialSelectedTeams: [],
  initialSelectedUsers: [],
  selectedTeams: [],
  selectedUsers: [],
  selectedTab: 'teams',
  sharingOption: EVERYONE
};
export default class AssignmentPanel extends Component {
  constructor(props) {
    super(props);
    this.open = ({
      objectId,
      objectType,
      ownerId
    }) => {
      if (!objectId) {
        if (this.props.isBulkEdit) {
          this.setState({
            sharingOption: null,
            initialSharingOption: null,
            isContentPermissionLoading: false,
            objectType,
            ownerId,
            isOpen: true
          });
          return;
        }
        // If no object is provided then assume the assignment is for a new object
        // and default to private permissions
        this.setState({
          sharingOption: PRIVATE,
          initialSharingOption: PRIVATE,
          isContentPermissionLoading: false,
          objectType,
          ownerId,
          isOpen: true
        });
        return;
      }
      this.setState({
        objectId,
        objectType,
        ownerId,
        isOpen: true
      });
      fetchPermissionsForContent({
        objectId,
        objectType
      }).then(permissionsData => {
        const selectedTeams = permissionsData.permissions.TEAM;
        const selectedUsers = permissionsData.permissions.USER;
        const sharingOption = permissionsData.visibleToAll ? EVERYONE : permissionsData.private ? PRIVATE : SPECIFIC;
        this.setState({
          selectedTeams,
          selectedUsers,
          sharingOption,
          initialSelectedTeams: selectedTeams,
          initialSelectedUsers: selectedUsers,
          initialSharingOption: sharingOption,
          isContentPermissionLoading: false
        });
      }, err => {
        this.setState({
          isContentPermissionError: true,
          isContentPermissionLoading: false
        });
        rethrowError(err);
      });
    };
    this.close = () => {
      this.setState(defaultState);
    };
    this.handleUserSearch = query => {
      this.setState({
        userSearch: query.toLowerCase()
      });
    };
    this.handleTeamChange = ({
      target: {
        value
      }
    }) => {
      const teamId = parseInt(value, 10);
      this.setState(({
        selectedTeams,
        teams
      }) => ({
        selectedTeams: toggleSelectedTeam(selectedTeams, teams, teamId)
      }));
    };
    this.handleUserChange = ({
      target: {
        value
      }
    }) => {
      const userId = parseInt(value, 10);
      this.setState(({
        selectedUsers
      }) => ({
        selectedUsers: toggleSelectUser(selectedUsers, userId)
      }));
    };
    this.handleSharingOptionChange = ({
      target: {
        value: sharingOption
      }
    }) => {
      const shouldUpdateSelectedUsers = sharingOption === SPECIFIC;
      this.setState(({
        ownerId,
        selectedUsers
      }) => {
        const ownersAsArray = Array.isArray(ownerId) ? ownerId : [ownerId];
        return {
          sharingOption: sharingOption,
          selectedUsers: shouldUpdateSelectedUsers ? selectedUsers.concat(ownersAsArray) : selectedUsers
        };
      });
    };
    this.handleTabChange = selectedTab => {
      this.setState({
        selectedTab
      });
    };
    this.handleConfirm = () => {
      const {
        objectId,
        objectType,
        sharingOption,
        selectedTeams,
        selectedUsers
      } = this.state;
      const {
        afterSave,
        onConfirm
      } = this.props;
      if (!sharingOption) {
        // Should never happen as the button should be disabled
        return;
      }
      const shareSpecific = sharingOption === SPECIFIC;
      const permissionsObject = {
        objectId,
        objectType: objectType,
        selectedTeams: shareSpecific ? selectedTeams : [],
        selectedUsers: shareSpecific ? selectedUsers : [],
        visibleToAll: sharingOption === EVERYONE,
        private: sharingOption === PRIVATE
      };
      if (onConfirm) {
        onConfirm(permissionsObject);
        this.setState(defaultState);
        return;
      }
      this.setState({
        isContentPermissionLoading: true
      });

      // @ts-expect-error objectId should not be null at this point
      saveContentPermissions(permissionsObject).then(() => {
        FloatingAlertStore.addAlert({
          type: 'success',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesContentPartitioning.alerts.successTitle"
          })
        });
        this.setState(defaultState);
        if (afterSave) {
          afterSave({
            sharingOption,
            selectedTeams,
            selectedUsers
          });
        }
      }, err => {
        FloatingAlertStore.addAlert({
          type: 'danger',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesContentPartitioning.alerts.errorTitle"
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesContentPartitioning.alerts.errorMessage"
          })
        });
        this.setState({
          isContentPermissionLoading: false
        });
        rethrowError(err);
      });
    };
    this.state = Object.assign({
      teams: null,
      usersForTeams: null
    }, defaultState);
  }
  componentDidMount() {
    const {
      onConfirm,
      afterSave,
      id
    } = this.props;
    AssignmentPanelInstances[id || DEFAULT_PANEL_ID] = this;
    invariant(!(onConfirm && afterSave), 'Only one of onConfirm and afterSave should be passed to AssignmentPanel as onConfirm overrides the save behavior');
    fetchUsersAndTeams(this.props.scopes).then(([teams, usersForTeams]) => {
      this.setState({
        teams,
        usersForTeams
      });
    }, error => {
      this.setState({
        isContentPermissionLoading: false,
        isContentPermissionError: true
      });
      rethrowError(error);
    });
  }
  componentWillUnmount() {
    delete AssignmentPanelInstances[this.props.id || DEFAULT_PANEL_ID];
  }
  renderBody() {
    const {
      children,
      isBulkEdit,
      scopes
    } = this.props;
    const {
      ownerId,
      selectedTab,
      teams,
      usersForTeams,
      selectedUsers,
      selectedTeams,
      sharingOption,
      userSearch,
      isContentPermissionLoading,
      isContentPermissionError,
      objectType
    } = this.state;
    if (isContentPermissionError || !objectType || !ownerId) {
      return /*#__PURE__*/_jsx(AssignmentPanelErrorState, {});
    }
    if (!teams || !usersForTeams || isContentPermissionLoading) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      });
    }
    const shouldShowTabs = sharingOption === SPECIFIC;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [children, /*#__PURE__*/_jsxs(Section, {
        children: [/*#__PURE__*/_jsx(AssignmentPanelOptions, {
          objectType: objectType,
          sharingOption: sharingOption,
          canAssignContent: canAssignContent(scopes),
          handleSharingOptionChange: this.handleSharingOptionChange,
          isBulkEdit: isBulkEdit
        }), shouldShowTabs && /*#__PURE__*/_jsx(AssignmentPanelTabs, {
          ownerId: ownerId,
          teams: teams,
          usersForTeams: usersForTeams,
          selectedTab: selectedTab,
          selectedTeams: selectedTeams,
          selectedUsers: selectedUsers,
          userSearch: userSearch,
          handleTeamChange: this.handleTeamChange,
          handleTabChange: this.handleTabChange,
          handleUserChange: this.handleUserChange,
          handleUserSearch: this.handleUserSearch,
          canAssignContent: canAssignContent(scopes)
        })]
      })]
    });
  }
  render() {
    if (!this.state.isOpen) {
      return null;
    }
    return /*#__PURE__*/_jsxs(ModalPanel, {
      "data-test-id": this.props.id || DEFAULT_PANEL_ID,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: this.close
        }), /*#__PURE__*/_jsx(H2, {
          children: this.props.title || /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesContentPartitioning.assignmentPanel.title.manage"
          })
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: this.renderBody()
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(AssignmentPanelSaveButton, {
          isContentPermissionError: this.state.isContentPermissionError,
          selectedTeams: this.state.selectedTeams,
          selectedUsers: this.state.selectedUsers,
          sharingOption: this.state.sharingOption,
          initialSelectedTeams: this.state.initialSelectedTeams,
          initialSelectedUsers: this.state.initialSelectedUsers,
          initialSharingOption: this.state.initialSharingOption,
          canAssignContent: canAssignContent(this.props.scopes),
          handleConfirm: this.handleConfirm,
          disabled: !!this.props.disableSave,
          disabledTooltipTitle: this.props.saveTooltipWhenDisabled,
          enableSaveWithoutPermissionChange: !this.state.objectId
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: this.close,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesContentPartitioning.assignmentPanel.footer.cancel"
          })
        })]
      })]
    });
  }
}
AssignmentPanel.propTypes = {
  children: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  isBulkEdit: PropTypes.bool,
  afterSave: PropTypes.func,
  onConfirm: PropTypes.func,
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'typeof List' is not assignable t... Remove this comment to see the full error message
  scopes: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.array]).isRequired,
  id: PropTypes.string
};
AssignmentPanel.defaultProps = {
  isBulkEdit: false,
  disableSave: false,
  scopes: []
};